import { useState, useEffect, useCallback } from 'react';
import { REACT_APP_DISCORD_BOT_ENDPOINT } from '../config/constants';

export default function UnifiedBackupForm({ authToken }) {
  const [backupType, setBackupType] = useState('');
  // Estados comuns
  const [submitted, setSubmitted] = useState(false);
  const [responseMsg, setResponseMsg] = useState('');
  const [backupDate, setBackupDate] = useState('');
  const [backup90Days, setBackup90Days] = useState(true);

  const [selectedBucket, setSelectedBucket] = useState('apikihost-backup-7dias-ubtajrcgzdcl');

  const [uploads, setUploads] = useState(true);

  // Buckets comuns
  const s3Buckets = [
    'wp-host-backups',
    'apikihost-backup-7dias-ubtajrcgzdcl'
  ];

  // --- RDS ---
  const [rdsList, setRdsList] = useState([]);
  const [rdsData, setRdsData] = useState({});
  const [selectedRds, setSelectedRds] = useState('');
  const [databases, setDatabases] = useState([]);
  const [selectedDb, setSelectedDb] = useState('');
  const [loadingDbs, setLoadingDbs] = useState(false);
  const [loadingRds, setLoadingRds] = useState(false);

  // --- Lightsail ---
  const [lightsailList, setLightsailList] = useState([]);
  const [loadingLightsail, setLoadingLightsail] = useState(false);
  const [selectedLightsail, setSelectedLightsail] = useState('');

  // --- Shared ---
  const [sharedList, setSharedList] = useState([]);
  const [loadingShared, setLoadingShared] = useState(false);
  const [selectedShared, setSelectedShared] = useState('');
  const [selectedHost, setSelectedHost] = useState('');
  const shareds = [
    { text: 'aws-shared-2', value: 'aws-shared-2' },
    { text: 'aws-shared-3', value: 'aws-shared-3' },
    { text: 'aws-shared-4', value: 'aws-shared-4' },
  ];

  // --- Private (EC2) ---
  const [ec2List, setEc2List] = useState([]);
  const [loadingEc2, setLoadingEc2] = useState(false);
  const [selectedEc2, setSelectedEc2] = useState('');

  // --- Funções de carregamento ---
  const loadRdsList = useCallback(async () => {
    setLoadingRds(true);
    try {
      const response = await fetch(REACT_APP_DISCORD_BOT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ command: 'aws', function: 'listAllRDS' })
      });
      const data = await response.json();
      if (data && data.DBInstances) {
        const rdsIdentifiers = data.DBInstances.map(instance => instance.DBInstanceIdentifier);
        setRdsList(rdsIdentifiers);
        const rdsEndpoints = {};
        data.DBInstances.forEach(instance => {
          if (instance.Endpoint && instance.Endpoint.Address) {
            rdsEndpoints[instance.DBInstanceIdentifier] = instance.Endpoint.Address;
          }
        });
        setRdsData(rdsEndpoints);
      } else {
        setRdsList([]);
        setRdsData({});
      }
    } catch {
      setRdsList([]);
      setRdsData({});
    } finally {
      setLoadingRds(false);
    }
  }, [authToken]);

  async function handleRdsChange(e) {
    const value = e.target.value;
    setSelectedRds(value);
    setDatabases([]);
    setSelectedDb('');
    if (value) {
      setLoadingDbs(true);
      try {
        const response = await fetch(REACT_APP_DISCORD_BOT_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            command: 'aws',
            function: 'queryRDSRemote',
            RDSPrivadoID: value,
            RDSPrivadoEndPoint: rdsData[value]
          })
        });
        const data = await response.json();
        let processedDatabases = [];
        if (Array.isArray(data)) {
          processedDatabases = data.map(item => Array.isArray(item) && item.length > 0 ? item[0] : item);
        } else if (typeof data === 'string') {
          try {
            const parsed = JSON.parse(data);
            if (Array.isArray(parsed)) {
              processedDatabases = parsed.map(item => Array.isArray(item) && item.length > 0 ? item[0] : item);
            }
          } catch {}
        }
        setDatabases(processedDatabases);
      } catch {
        setDatabases([]);
      } finally {
        setLoadingDbs(false);
      }
    }
  }

  const loadLightsailList = useCallback(async () => {
    setLoadingLightsail(true);
    try {
      const response = await fetch(REACT_APP_DISCORD_BOT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ command: 'aws', function: 'listAllLightsail' })
      });
      const data = await response.json();
      const ResourcesList = [];
      data.forEach(e => {
        const ls = { text: e.name, value: `${e.name}|${e.bundleId}|${e.arn}` };
        ResourcesList.push(ls);
      });
      ResourcesList.sort((a, b) => a.text.localeCompare(b.text, 'pt-BR'));
      setLightsailList(ResourcesList);
    } catch {
      setLightsailList([]);
    } finally {
      setLoadingLightsail(false);
    }
  }, [authToken]);

  async function loadHostsList(shared) {
    setLoadingShared(true);
    try {
      const response = await fetch(REACT_APP_DISCORD_BOT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ command: 'opt', function: 'list-hosts-shareds', shared })
      });
      const data = await response.json();
      setSharedList(data);
    } catch {
      setSharedList([]);
    } finally {
      setLoadingShared(false);
    }
  }

  const loadEc2List = useCallback(async () => {
    setLoadingEc2(true);
    try {
      const response = await fetch(REACT_APP_DISCORD_BOT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ command: 'aws', function: 'listAllEC2' })
      });
      const data = await response.json();
      const ResourcesList = [];
      data.forEach(e => {
        let _resource;
        const hostName = e[0].Tags.filter(e => e.Key === 'host');
        const workspace = e[0].Tags.filter(e => e.Key === 'workspace');
        const product = e[0].Tags.filter(e => e.Key === 'product');
        const act = e[0].Tags.filter(e => e.Key === 'act');
        if (workspace.length !== 0 && workspace[0].Value === 'internal') return;
        if (product.length !== 0 && product[0].Value === 'wphost-shared') return;
        if (hostName.length === 0) {
          _resource = {
            text: `${e[0].InstanceId}`,
            value: `${e[0].InstanceId}|${e[0].InstanceType}|${e[0].Architecture}`
          };
        } else {
          const actFilter = act.length !== 0 ? `${act[0].Value}.` : '';
          _resource = {
            text: `${actFilter}${hostName[0].Value} - ${e[0].InstanceId}`,
            value: `${e[0].InstanceId}|${e[0].InstanceType}|${e[0].Architecture}|${hostName[0].Value}`
          };
        }
        ResourcesList.push(_resource);
      });
      ResourcesList.sort((a, b) => a.text.localeCompare(b.text, 'pt-BR'));
      setEc2List(ResourcesList);
    } catch {
      setEc2List([]);
    } finally {
      setLoadingEc2(false);
    }
  }, [authToken]);

  // Carregar listas ao trocar tipo
  useEffect(() => {
    if (backupType === 'rds') loadRdsList();
    if (backupType === 'lightsail') loadLightsailList();
    if (backupType === 'shared') setSharedList([]); // Limpa ao trocar
    if (backupType === 'private') loadEc2List();
    // Limpa campos ao trocar
    setSubmitted(false);
    setResponseMsg('');
    setBackupDate('');
    setBackup90Days(true); // Mantém marcado por padrão
    setSelectedBucket('apikihost-backup-7dias-ubtajrcgzdcl');
    setUploads(true); // Mantém marcado por padrão
    setSelectedRds('');
    setDatabases([]);
    setSelectedDb('');
    setSelectedLightsail('');
    setSelectedShared('');
    setSelectedHost('');
    setSelectedEc2('');
  }, [backupType, loadRdsList, loadLightsailList, loadEc2List]);

  const handleBucketChange = (e) => {
    const newBucket = e.target.value;
    const defaultBucket = 'apikihost-backup-7dias-ubtajrcgzdcl';

    if (selectedBucket === defaultBucket && newBucket !== defaultBucket) {
      const confirmed = window.confirm(
        `Você está alterando o bucket padrão '${defaultBucket}' para '${newBucket}'. Tem certeza que deseja continuar?`
      );

      if (confirmed || newBucket === defaultBucket) {
        setSelectedBucket(newBucket);
      }
    } else {
      setSelectedBucket(newBucket);
    }
  };

  // --- Submissão ---
  const handleSubmit = (e) => {
    e.preventDefault();
    let formattedBackupDate = backupDate;
    if (/^\d{4}-\d{2}-\d{2}$/.test(backupDate)) {
      const [year, month, day] = backupDate.split('-');
      formattedBackupDate = `${day}/${month}/${year}`;
    }
    let data = {};
    if (backupType === 'rds') {
      data = {
        form: 'database_generate_backup',
        rds: selectedRds,
        database: selectedDb,
        backupDate: formattedBackupDate,
        backup90Days,
        bucket: selectedBucket
      };
    } else if (backupType === 'lightsail') {
      data = {
        form: 'lightsail_generate_backup',
        lightsail: selectedLightsail,
        uploads,
        backup90days: backup90Days,
        bucket: selectedBucket,
        backupDate: formattedBackupDate
      };
    } else if (backupType === 'shared') {
      data = {
        form: 'shared_generate_backup',
        shared: selectedShared,
        host: selectedHost,
        uploads,
        backup90days: backup90Days,
        bucket: selectedBucket,
        backupDate: formattedBackupDate
      };
    } else if (backupType === 'private') {
      data = {
        form: 'private_generate_backup',
        ec2: selectedEc2,
        uploads,
        backup90days: backup90Days,
        bucket: selectedBucket,
        backupDate: formattedBackupDate
      };
    }
    fetch(REACT_APP_DISCORD_BOT_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ command: 'submit', data })
    })
      .then((res) => res.text())
      .then((msg) => {
        setSubmitted(true);
        setResponseMsg(msg);
      });
  };

  if (submitted) {
    let content;
    try {
      const parsed = JSON.parse(responseMsg);
      content = (
        <div className="json-response-list">
          {Object.entries(parsed).map(([key, value]) => (
            <div key={key} style={{ marginBottom: '0.5em' }}>
              <strong>{key}:</strong> <span>{String(value)}</span>
            </div>
          ))}
        </div>
      );
    } catch {
      content = <p>{responseMsg}</p>;
    }
    return (
      <div className="dynamic-form">
        <div className="success-message">
          {content}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="dynamic-form">
      <div className="form-group">
        <label className="form-label">Tipo de Backup</label>
        <select value={backupType} onChange={e => setBackupType(e.target.value)} className="form-select">
          <option value="">Selecione o tipo</option>
          <option value="rds">Banco de Dados</option>
          <option value="lightsail">Lightsail</option>
          <option value="shared">Shared</option>
          <option value="private">Private</option>
        </select>
      </div>

      {/* RDS */}
      {backupType === 'rds' && (
        <div style={{border: '1px solid #eee', padding: 16, borderRadius: 8, marginBottom: 16}}>
          <div className="form-group">
            <label className="form-label">Servidor RDS</label>
            {loadingRds ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="loading-text">Carregando servidores RDS...</p>
              </div>
            ) : (
              <select value={selectedRds} onChange={handleRdsChange} className="form-select">
                <option value="">Selecione um servidor</option>
                {rdsList.map((rds) => (
                  <option key={rds} value={rds}>
                    {rds} {rdsData[rds] && `(${rdsData[rds]})`}
                  </option>
                ))}
              </select>
            )}
          </div>
          {loadingDbs && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p className="loading-text">Carregando bancos de dados...</p>
            </div>
          )}
          {databases.length > 0 && (
            <div className="form-group">
              <label className="form-label">Banco de Dados</label>
              <select value={selectedDb} onChange={e => setSelectedDb(e.target.value)} className="form-select">
                <option value="">Selecione um banco</option>
                {databases.map((db) => (
                  <option key={db} value={db}>{db}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Lightsail */}
      {backupType === 'lightsail' && (
        <div style={{border: '1px solid #eee', padding: 16, borderRadius: 8, marginBottom: 16}}>
          <div className="form-group">
            <label className="form-label">Selecione um domínio</label>
            {loadingLightsail ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="loading-text">Carregando domínios...</p>
              </div>
            ) : (
              <select value={selectedLightsail} onChange={e => setSelectedLightsail(e.target.value)} className="form-select">
                <option value="">Selecione um domínio</option>
                {lightsailList.map((lightsail) => (
                  <option key={lightsail.value} value={lightsail.value}>{lightsail.text}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}

      {/* Shared */}
      {backupType === 'shared' && (
        <div style={{border: '1px solid #eee', padding: 16, borderRadius: 8, marginBottom: 16}}>
          <div className="form-group">
            <label className="form-label">Shared</label>
            <select value={selectedShared} onChange={e => { setSelectedShared(e.target.value); setSelectedHost(''); loadHostsList(e.target.value); }} className="form-select">
              <option value="">Selecione o Shared</option>
              {shareds.map((shared) => (
                <option key={shared.value} value={shared.value}>{shared.text}</option>
              ))}
            </select>
          </div>
          {loadingShared && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p className="loading-text">Carregando hosts do Shared...</p>
            </div>
          )}
          {sharedList.length > 0 && (
            <div className="form-group">
              <label className="form-label">Domínio</label>
              <select value={selectedHost} onChange={e => setSelectedHost(e.target.value)} className="form-select">
                <option value="">Selecione um Domínio</option>
                {sharedList.map((shared) => (
                  <option key={shared.value} value={shared.value}>{shared.text}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Private */}
      {backupType === 'private' && (
        <div style={{border: '1px solid #eee', padding: 16, borderRadius: 8, marginBottom: 16}}>
          <div className="form-group">
            <label className="form-label">Selecione um domínio</label>
            {loadingEc2 ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="loading-text">Carregando domínios...</p>
              </div>
            ) : (
              <select value={selectedEc2} onChange={e => setSelectedEc2(e.target.value)} className="form-select">
                <option value="">Selecione um domínio</option>
                {ec2List.map((ec2) => (
                  <option key={ec2.value} value={ec2.value}>{ec2.text}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}

      {/* Campos comuns */}
      <div className="form-group">
        <label className="form-label">Data do Backup</label>
        <input
          type="date"
          value={backupDate}
          onChange={e => setBackupDate(e.target.value)}
          max={new Date().toISOString().split('T')[0]}
          min={new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
          className="form-input"
        />
        <small className="form-help">Máximo de 7 dias atrás</small>
      </div>

      <div className="form-group">
        <label className="form-label checkbox-label">
          <input
            type="checkbox"
            checked={backup90Days}
            onChange={e => setBackup90Days(e.target.checked)}
            className="form-checkbox"
          />
          <span className="checkbox-text">Fazer backup de 90 dias</span>
        </label>
      </div>

      {/* Uploads só para Lightsail, Shared, Private */}
      {(backupType === 'lightsail' || backupType === 'shared' || backupType === 'private') && (
        <div className="form-group">
          <label className="form-label checkbox-label">
            <input
              type="checkbox"
              checked={uploads}
              onChange={e => setUploads(e.target.checked)}
              className="form-checkbox"
            />
            <span className="checkbox-text">Fazer backup de Uploads</span>
          </label>
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Bucket S3</label>
        <select
          value={selectedBucket}
          onChange={handleBucketChange}
          className="form-select"
        >
          <option value="">Selecione um bucket</option>
          {s3Buckets.map((bucket) => (
            <option key={bucket} value={bucket}>{bucket}</option>
          ))}
        </select>
      </div>

      <div className="form-actions">
        <button
          type="submit"
          disabled={
            !backupType ||
            (backupType === 'rds' && (!selectedRds || !selectedDb || !backupDate || !selectedBucket)) ||
            (backupType === 'lightsail' && (!selectedLightsail || !selectedBucket || !backupDate)) ||
            (backupType === 'shared' && (!selectedShared || !selectedHost || !selectedBucket || !backupDate)) ||
            (backupType === 'private' && (!selectedEc2 || !selectedBucket || !backupDate))
          }
          className="submit-button"
        >
          Continuar
        </button>
      </div>
    </form>
  );
}